import { Parameter$AdminDocumentController_findAll } from '@api-client/generated/client';

/**
 * Builds the route to a single expense document's details page.
 *
 * Centralised so the document list and the details page (prev/next navigation)
 * stay in sync on the same URL shape.
 *
 * @param companyId - Id of the company the document belongs to.
 * @param documentId - Id of the document to open.
 * @returns The absolute app route, e.g. `/companies/c1/documents/bob50/d1`.
 */
export const getDocumentExpenseDetailsPath = (
  companyId: string,
  documentId: string
): string => `/companies/${companyId}/documents/bob50/${documentId}`;

/**
 * The effective document-list query (filters + sort, without `page`) used to
 * fetch additional pages of ids on demand from the details page.
 */
export type DocumentListQuery = Omit<
  Parameter$AdminDocumentController_findAll,
  'page'
>;

/**
 * React Router state carried from the document list into the details page so it
 * can offer prev/next navigation across the operator's filtered list.
 *
 * The state is ephemeral (React Router in-memory state): it is lost on a full
 * reload, a copied URL, or opening a document in a new tab. In those cases the
 * details page falls back to disabled navigation buttons.
 */
export type DocumentListNavigationState = {
  /** The list's URL search string, replayed when returning to the list. */
  fromSearch?: string;
  /** Ordered ids already loaded into the list (grows as more pages are fetched). */
  documentIds?: string[];
  /** Query used to fetch more ids when the operator reaches the loaded edge. */
  documentQuery?: DocumentListQuery;
  /** Next page to fetch at the loaded edge, or `null` when the list is fully loaded. */
  nextPage?: number | null;
};
